import React, { useMemo, useState } from 'react'
import { makeStyles, Typography, useTheme } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { RenderableTotals } from '@paintscout/util/builder'
import { getTierPrices, getTotals, TIER_LABELS } from '@paintscout/util/builder'
import type { TieredPackageTierId } from 'paintscout'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { useQuote } from '../../../../context'
import { TierMark } from './shared'
import PackageCompareDialog from './PackageCompareDialog'
import { useClientOptions } from '@paintscout/ui-business'

const useStyles = makeStyles<Theme>(
  (theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5)
      },
      labelRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        color: neutral.P60
      },
      label: {
        fontSize: '14px !important'
      },
      // "Switch >" affordance — muted link that opens the Compare plans dialog. Hidden in print/PDF
      // since it can't be used on paper.
      switchButton: {
        appearance: 'none',
        border: 'none',
        background: 'transparent',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.25),
        cursor: 'pointer',
        font: 'inherit',
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P60,
        '&:hover': {
          color: neutral.P40
        },
        '@media print': {
          display: 'none'
        }
      },
      switchIcon: {
        fontSize: 18,
        marginRight: -4,
        marginTop: 1
      },
      tierRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2)
      },
      tierName: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P35,
        fontWeight: theme.typography.fontWeightMedium
      },
      seal: {
        display: 'inline-flex',
        flexShrink: 0
      },
      delta: {
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P60,
        fontWeight: theme.typography.fontWeightMedium,
        whiteSpace: 'nowrap'
      }
    }
  },
  { name: 'PackageSelectedRow' }
)

/**
 * Compact "Package Selected" summary shown inside the Total card when tiered packages are set up:
 * the label + a "Switch" affordance, then the selected tier (seal + name) and the package's own
 * value. "Switch" opens the {@link PackageCompareDialog}; picking a tier there applies
 * it and recalculates through the surrounding quote context (persists in the editor, local-only in
 * the customer/preview context). Renders nothing when packages aren't enabled.
 */
export default function PackageSelectedRow() {
  const classes = useStyles({})
  const { options } = useClientOptions()
  const theme = useTheme()
  const { quote, isEditable } = useQuote()
  const [dialogOpen, setDialogOpen] = useState(false)

  const tierPrices = useMemo(() => getTierPrices({ quote }), [quote])
  const tieredPackages = quote.tieredPackages

  const totals = getTotals({ quote, consumer: 'customer', options }) as RenderableTotals
  const { subTotal } = totals
  if (!tieredPackages?.enabled || !tierPrices) {
    return null
  }

  // Customers lose the Switch once they've responded (accepted/declined) or the quote became an
  // invoice — mirrors the read-only lock in TieredPackageSelector. The contractor editor
  // (isEditable) can always switch, regardless of status.
  const isLocked = quote.status?.value === 'accepted' || quote.status?.value === 'declined' || !!quote.is_invoice
  const canSwitch = isEditable || !isLocked

  const selectedTierId = (tieredPackages.selectedTierId ?? tieredPackages.baseTierId) as TieredPackageTierId
  const selected = tierPrices[selectedTierId]
  if (!selected) {
    return null
  }

  const selectedName = selected.name || TIER_LABELS[selectedTierId]

  return (
    <div className={classes.root}>
      <div className={classes.labelRow}>
        <Typography variant="body2" className={classes.label}>
          Package Selected
        </Typography>
        {canSwitch && (
          <button type="button" className={classes.switchButton} onClick={() => setDialogOpen(true)}>
            Switch
            <ChevronRightIcon className={classes.switchIcon} />
          </button>
        )}
      </div>

      <div className={classes.tierRow}>
        <span className={classes.tierName}>
          <span className={classes.seal}>
            <TierMark color={theme.paintscout.palette.brand.Neutral.P30} size={16} />
          </span>
          {selectedName}
        </span>
        <span className={classes.delta}>
          <Typography
            component="span"
            value={subTotal - selected.price}
            format="price"
            showUnits
            showZeros
            variant="body1"
          />
        </span>
      </div>

      {canSwitch && <PackageCompareDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />}
    </div>
  )
}
